<template>
  <div>
    <b-modal
      centered
      ref="dialogWait"
      no-close-on-backdrop
      no-close-on-esc
      size="sm"
      hide-header
      hide-footer
    >
      <div class="center-xs"><spinner /></div>
      <div class="center-xs pt-3">{{ currentLoadingMessage }}</div>
    </b-modal>

    <div v-if="!isAdmin" class="p-4 text-center text-muted">
      A edição de dados da estação é restrita a administradores.
    </div>

    <template v-else>
      <div class="w-100 d-flex flex-wrap align-items-center justify-content-between mb-2" style="gap: 0.5rem 1rem">
        <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem">
          <div class="d-flex align-items-center" style="gap: 0.25rem">
            <small class="text-muted text-nowrap">Ir para</small>
            <b-form-input
              v-model="focusDate"
              type="date"
              size="sm"
              style="width: 150px"
              :max="today"
              @change="loadWindow"
            />
          </div>
          <b-btn
            variant="3v3"
            size="sm"
            class="saveButton font-weight-regular"
            :disabled="!editedPreview.length"
            @click="openSaveModal"
          >
            Salvar dados
          </b-btn>
          <b-button
            v-if="focusDate !== today"
            size="sm"
            variant="outline-secondary"
            @click="goToToday"
          >
            Voltar para hoje
          </b-button>
        </div>

        <b-button size="sm" variant="outline-danger" @click="openRecalcModal">
          Reprocessar estação
        </b-button>
      </div>

      <data-table
        ref="dataTable"
        sticky-header
        class="flex-grow-1"
        max-height-table="100%"
        :editable="isAdmin"
        :left-frozen-columns="leftFrozenColumns"
        :static-columns="staticColumns"
        :table-data="items"
        :row-formatter="rowFormatterFn"
        @update-table-data="updateTableData"
        :file-prefix="`ETo_MANEJO_${startDate}_${endDate}`"
      />

      <div class="d-flex align-items-center justify-content-between mt-2">
        <b-button size="sm" variant="outline-secondary" @click="shiftWindow(-1)">
          &laquo; Voltar
        </b-button>
        <small class="text-muted text-center" style="font-size: 0.85em">
          Exibindo ETo e precipitação de <strong>{{ formatDate(startDate) }}</strong> até
          <strong>{{ formatDate(endDate) }}</strong>.
        </small>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="focusDate >= today"
          @click="shiftWindow(1)"
        >
          Avançar &raquo;
        </b-button>
      </div>
    </template>

    <!-- Modal Ação A: salvar + recalcular -->
    <b-modal
      ref="saveModal"
      centered
      title="Confirmar alterações"
      ok-title="Salvar e recalcular"
      ok-variant="success"
      cancel-title="Cancelar"
      cancel-variant="outline-secondary"
      @ok="confirmSave"
    >
      <div v-if="saveEntries.length">
        <p class="mb-2">{{ saveEntries.length }} dia(s) serão salvos.</p>

        <b-alert v-if="saveGap && saveGap.firstGap" show variant="warning" class="py-2">
          <template v-if="saveReachesEdits">
            O manejo será recalculado de <strong>{{ formatDate(saveFromDate) }}</strong> até
            <strong>{{ formatDate(saveGap.willReach) }}</strong
            >, e para em <strong>{{ formatDate(saveGap.firstGap) }}</strong
            >, que segue sem ETo.<template v-if="saveEntriesAfterGap">
              Os dias inseridos após entram no cálculo quando o salto de ETo for
              preenchido.</template
            >
          </template>
          <template v-else>
            O manejo <strong>não avança</strong>: ele para em
            <strong>{{ formatDate(saveGap.firstGap) }}</strong
            >, que segue sem ETo. Os dias inseridos após entram no cálculo quando o salto de ETo for
              preenchido.
          </template>
        </b-alert>
        <p v-else class="mb-2">
          O manejo será recalculado de <strong>{{ formatDate(saveFromDate) }}</strong
          ><template v-if="saveGap && saveGap.willReach">
            até <strong>{{ formatDate(saveGap.willReach) }}</strong></template
          >.
        </p>
        <ul class="day-block">
          <li v-for="e in saveEntries" :key="e.time">
            {{ formatDate(e.time) }} — ETo: {{ e.et0 }} · Chuva: {{ e.rainfall }}
          </li>
        </ul>
        <small class="text-muted">Esta ação será registrada em seu nome.</small>
      </div>
      <div v-else class="text-muted">Nenhuma alteração para salvar.</div>
    </b-modal>

    <!-- Modal Ação B: reprocessar a partir de X (apaga manuais no range) -->
    <b-modal
      ref="recalcModal"
      centered
      title="Reprocessar estação"
      :ok-title="recalcRestorable.length ? 'Reprocessar estação' : 'Somente recalcular'"
      :ok-variant="recalcRestorable.length ? 'danger' : 'secondary'"
      cancel-title="Cancelar"
      cancel-variant="outline-secondary"
      :ok-disabled="!isValidRecalcDate"
      @ok="confirmRecalc"
      @hidden="resetRecalc"
    >
      <b-form-group label="De:" class="font-weight-bold mb-2">
        <b-form-input
          v-model="recalcFromDate"
          type="date"
          :max="yesterday"
          :state="recalcFromDate ? isValidRecalcDate : null"
          @input="onRecalcDateChange"
        />
        <b-form-invalid-feedback :state="recalcFromDate ? isValidRecalcDate : null">
          Escolha uma data anterior a hoje.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Até:" class="font-weight-bold mb-3">
        <b-form-input
          v-model="recalcToDate"
          type="date"
          :min="recalcFromDate"
          :max="yesterday"
          :disabled="!isValidRecalcDate"
          @input="onRecalcDateChange"
        />
        <small class="text-muted">Em branco, vai até o último dia com dados.</small>
      </b-form-group>

      <template v-if="isValidRecalcDate">
        <p class="mb-2">
          Em <strong>{{ recalcWindowLabel }}</strong>, os dias medidos pela estação
          <strong>voltam à medição original</strong>. Sem medição, o valor digitado é mantido.
        </p>
        <div v-if="recalcRestorable.length">
          <p class="mb-1">{{ recalcRestorable.length }} dia(s) voltarão à medição original:</p>
          <ul class="day-block">
            <li v-for="d in recalcRestorable" :key="d.TIME">
              {{ formatDate(d.TIME) }} — {{ d.FALLBACK_HOURS }}h medidas
            </li>
          </ul>
        </div>
        <b-alert v-if="recalcKept.length" show variant="info" class="py-2">
          <div class="mb-1">
            {{ recalcKept.length }} dia(s) sem medição: o valor digitado <strong>será mantido</strong>.
          </div>
          <ul class="day-block day-block--flush">
            <li v-for="d in recalcKept" :key="d.TIME">{{ formatDate(d.TIME) }}</li>
          </ul>
        </b-alert>
        <p v-if="!recalcPreview.length" class="text-muted">
          Nada digitado no período — só recalcula.
        </p>
        <small class="text-muted">Esta ação será registrada em seu nome.</small>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import { cookies } from "@/pages/components/cookies";
import DataTable from "@/pages/components/shared/table/DataTable.vue";
import { stations } from "@/api";

const SOURCE_COLORS = { MANUAL: "#3D9161", API: "#6c757d", STATION: "#4a90d9" };
const SOURCE_LABELS = { STATION: "EMR", MANUAL: "Manual", API: "METOS" };
const HALF_WINDOW = 7; // dia buscado ao centro: 7 antes + dia + 7 depois = 15
const FROZEN_TITLE = "Valor do dia inteiro — a estação não mediu hora a hora";
const MISSING_TITLE = "Dia sem ETo — o manejo para aqui até ser preenchido";
const MISSING_TAG =
  '<span style="background:#ffc107;color:#212529;border-radius:4px;padding:1px 6px;font-size:0.8em">Faltante</span>';
const ROW_STATE_CLASSES = ["st-locked", "st-missing", "st-edited"];
const SPINNER_DELAY = 250;

export default {
  components: { DataTable },
  data() {
    return {
      focusDate: moment().format("YYYY-MM-DD"),
      recalcFromDate: "",
      recalcToDate: "",
      saveEntries: [],
      saveGap: null,
      firstEt0: null,
      currentLoadingMessage: "",
      loading: false,
      spinnerTimer: null,
      items: [],
      itemsUnchanged: [],
      // Edições ainda não salvas, por dia: sobrevivem à troca de janela e só somem ao salvar ou sair da tela.
      pendingEdits: {},
      recalcPreview: [],
      leftFrozenColumns: [
        {
          title: "Dia",
          field: "time",
          minWidth: 120,
          formatter: (cell) => moment(cell.getValue()).format("DD/MM/YYYY"),
          accessorDownload: (val) => moment(val).format("DD/MM/YYYY"),
        },
      ],
    };
  },
  watch: {
    // Paginar carrega em dezenas de ms: abrir o modal na hora só faz o backdrop piscar. Ele espera
    // SPINNER_DELAY e nem chega a aparecer quando a resposta é rápida.
    loading(v) {
      clearTimeout(this.spinnerTimer);
      if (!v) return this.$refs.dialogWait.hide();
      this.spinnerTimer = setTimeout(() => this.$refs.dialogWait.show(), SPINNER_DELAY);
    },
  },
  beforeDestroy() {
    clearTimeout(this.spinnerTimer);
  },
  computed: {
    isAdmin() {
      return Number(cookies.get("PERMISSION")) === 1;
    },
    today() {
      return moment().format("YYYY-MM-DD");
    },
    yesterday() {
      return moment().subtract(1, "days").format("YYYY-MM-DD");
    },
    startDate() {
      return moment(this.focusDate).subtract(HALF_WINDOW, "days").format("YYYY-MM-DD");
    },
    endDate() {
      return moment(this.focusDate).add(HALF_WINDOW, "days").format("YYYY-MM-DD");
    },
    isValidRecalcDate() {
      return !!this.recalcFromDate && moment(this.recalcFromDate).isBefore(moment().startOf("day"));
    },
    editedPreview() {
      return this.getEditedEntries();
    },
    recalcRestorable() {
      return this.recalcPreview.filter((d) => Number(d.WILL_RESTORE) === 1);
    },
    recalcKept() {
      return this.recalcPreview.filter((d) => Number(d.WILL_RESTORE) !== 1);
    },
    recalcWindowLabel() {
      const from = this.formatDate(this.recalcFromDate);
      return this.recalcToDate ? `${from} até ${this.formatDate(this.recalcToDate)}` : `${from} em diante`;
    },
    saveFromDate() {
      const times = this.saveEntries.map((e) => e.time).sort();
      return times[0];
    },
    // O balanço quebra no vazio: se ele para antes do 1º dia digitado, nada do que foi salvo é calculado.
    saveReachesEdits() {
      return Boolean(this.saveGap && this.saveGap.willReach >= this.saveFromDate);
    },
    saveEntriesAfterGap() {
      const gap = this.saveGap && this.saveGap.firstGap;
      return Boolean(gap) && this.saveEntries.some((e) => e.time > gap);
    },
    staticColumns() {
      return [
        {
          title: "ETo",
          field: "et0_value",
          minWidth: 90,
          editor: "number",
          editable: (cell) => this.isAdmin && this.isEditableDay(cell.getData()),
          accessorDownload: (val) => (val === null ? "indefinido" : val),
          cellEdited: (cell) => this.recolorAllRows(cell.getTable()),
          formatter: (cell) => {
            const missing = cell.getValue() == null && this.isEditableDay(cell.getRow().getData());
            cell.getElement().title = missing ? MISSING_TITLE : "";
            return cell.getValue() == null ? "" : cell.getValue();
          },
        },
        {
          title: "Precipitação",
          field: "rainfall_value",
          minWidth: 90,
          editor: "number",
          editable: (cell) => this.isAdmin && this.isEditableDay(cell.getData()),
          cellEdited: (cell) => this.recolorAllRows(cell.getTable()),
        },
        {
          title: "Cobertura (h)",
          field: "covered_hours",
          minWidth: 90,
          editable: false,
          // Só a estação mede por hora; em MANUAL/API o valor é a chave de prioridade interna (chega a 25).
          formatter: (cell) => {
            const { source_kind: source } = cell.getRow().getData();
            const frozen = Boolean(source) && source !== "STATION";
            const el = cell.getElement();
            el.classList.toggle("cell-frozen", frozen);
            el.title = frozen ? FROZEN_TITLE : "";
            if (frozen) return "";
            return cell.getValue() == null ? "—" : cell.getValue();
          },
          accessorDownload: (val, data) =>
            data && data.source_kind && data.source_kind !== "STATION" ? "" : val,
        },
        {
          title: "Fonte",
          field: "source_kind",
          minWidth: 90,
          editable: false,
          formatter: (cell) => {
            const v = cell.getValue();
            if (!v) return this.isEditableDay(cell.getRow().getData()) ? MISSING_TAG : "—";
            const c = SOURCE_COLORS[v] || "#6c757d";
            const label = SOURCE_LABELS[v] || v;
            return `<span style="background:${c};color:#fff;border-radius:4px;padding:1px 6px;font-size:0.8em">${label}</span>`;
          },
        },
      ];
    },
  },
  async created() {
    this.$emit("title-change", "Estação Manual");
    if (this.isAdmin) await this.loadWindow();
  },
  methods: {
    // Lidos pelo ActionButtonModal ao fechar a planilha; nada a perguntar = pode fechar.
    closeQuestion() {
      const pending = this.editedPreview.length;
      return pending
        ? `${pending} dia(s) com valores digitados ainda não foram salvos. O que deseja fazer?`
        : null;
    },
    requestSave() {
      this.openSaveModal();
    },
    formatDate(t) {
      return t ? moment(t).format("DD/MM/YYYY") : "—";
    },
    normalize(v) {
      return v === null || v === undefined || v === "" ? null : Number(v);
    },
    isEditableDay(item) {
      return item && item.time < this.today;
    },
    isEdited(item) {
      const before = this.itemsUnchanged.find((u) => u.time === item.time) || {};
      return (
        this.normalize(item.et0_value) !== this.normalize(before.et0_value) ||
        this.normalize(item.rainfall_value) !== this.normalize(before.rainfall_value)
      );
    },
    // Cinza = hoje/futuro (não editável); amarelo = falta a ETo; verde = digitado e ainda não salvo.
    // A origem do dado já salvo fica só na tag da coluna Fonte.
    rowState(item) {
      if (!this.isEditableDay(item)) return "locked";
      if (this.normalize(item.et0_value) === null) return "missing";
      return this.isEdited(item) ? "edited" : null;
    },
    // Classe e não style inline: o inline venceria o :hover do Tabulator e mataria o realce da linha.
    applyRowColor(row) {
      const el = row.getElement();
      const state = this.rowState(row.getData());
      el.classList.remove(...ROW_STATE_CLASSES);
      if (state) el.classList.add(`st-${state}`);
      el.style.fontWeight = "";
      el.style.backgroundColor = "";
      el.style.color = state === "locked" ? "#555" : "";
    },
    rowFormatterFn(row) {
      this.applyRowColor(row);
    },
    recolorAllRows(table) {
      table.getRows().forEach((r) => this.applyRowColor(r));
    },
    updateTableData(val) {
      this.items = val;
    },
    // Guardadas + as da janela visível, que são a versão corrente do dia (inclusive quando o valor volta
    // ao original e a edição deixa de existir).
    mergedEdits() {
      const merged = { ...this.pendingEdits };
      for (const item of this.items) {
        if (this.isEdited(item)) {
          merged[item.time] = { et0_value: item.et0_value, rainfall_value: item.rainfall_value };
        } else {
          delete merged[item.time];
        }
      }
      return merged;
    },
    getEditedEntries() {
      const merged = this.mergedEdits();
      return Object.keys(merged)
        .sort()
        .map((time) => ({
          time,
          et0: merged[time].et0_value,
          rainfall: merged[time].rainfall_value,
        }));
    },
    dayArray(start, end) {
      const days = moment(end).diff(start, "days") + 1;
      return [...Array(Math.max(days, 0)).keys()].map((i) => ({
        time: moment(start).add(i, "days").format("YYYY-MM-DD"),
      }));
    },
    async loadWindow({ keepEdits = true } = {}) {
      try {
        // Recolhe o que está na tela antes de trocar a janela; após salvar, o servidor já é a verdade.
        this.pendingEdits = keepEdits ? this.mergedEdits() : {};
        this.loading = true;
        this.currentLoadingMessage = "Carregando dados da estação...";
        const { rows, firstEt0 } = await stations.getStationManualSeries({
          from: this.startDate,
          to: this.endDate,
        });
        this.firstEt0 = firstEt0;
        const fromServer = this.dayArray(this.startDate, this.endDate).map((row) => {
          const r = (rows || []).find((x) => x.TIME === row.time);
          return {
            time: row.time,
            et0_value: r ? r.ET0_VALUE : null,
            rainfall_value: r ? r.RAINFALL_VALUE : null,
            covered_hours: r ? r.ET0_HOURS : null,
            source_kind: r ? r.ET0_SOURCE : null,
          };
        });
        this.itemsUnchanged = JSON.parse(JSON.stringify(fromServer));
        this.items = fromServer.map((row) =>
          this.pendingEdits[row.time] ? { ...row, ...this.pendingEdits[row.time] } : row
        );
      } catch (e) {
        alert("Erro ao carregar dados da estação", "e");
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    shiftWindow(dir) {
      const step = 2 * HALF_WINDOW + 1;
      let next = moment(this.focusDate).add(dir * step, "days");
      const cap = moment(this.today);
      if (next.isAfter(cap)) next = cap;
      this.focusDate = next.format("YYYY-MM-DD");
      this.loadWindow();
    },
    goToToday() {
      this.focusDate = this.today;
      this.loadWindow();
    },
    // ETo é obrigatória: chuva sem ETo é inválida; ETo sem chuva resolve chuva=0.
    buildSave() {
      const entries = [];
      const invalid = [];
      for (const e of this.getEditedEntries()) {
        const et0 = this.normalize(e.et0);
        const rain = this.normalize(e.rainfall);
        if (et0 === null && rain === null) continue; // limpeza: nada a inserir
        if (et0 === null) { invalid.push(e.time); continue; }
        entries.push({ time: e.time, et0, rainfall: rain === null ? 0 : rain });
      }
      return { entries, invalid };
    },
    // O backend refaz a conta do break do balanço com estes dias já gravados, e devolve onde o manejo para.
    async computeSaveGap(entries) {
      try {
        return await stations.getManagementGap(entries.map((e) => e.time));
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    async openSaveModal() {
      const { entries, invalid } = this.buildSave();
      if (invalid.length) {
        return alert(
          `É necessário informar o valor de ETo para: ${invalid.map((t) => this.formatDate(t)).join(", ")}`,
          "e"
        );
      }
      if (!entries.length) return alert("Nenhuma alteração para salvar", "s");
      this.saveEntries = entries;
      this.saveGap = await this.computeSaveGap(entries);
      this.$refs.saveModal.show();
    },
    async confirmSave() {
      try {
        this.loading = true;
        this.currentLoadingMessage = "Salvando e recalculando manejo...";
        // O backend salva e recompõe o manejo na mesma requisição (até o último ET0 utilizável).
        await stations.saveManualStationData(this.saveEntries);
        await this.loadWindow({ keepEdits: false });
        alert("Sucesso ao salvar e recalcular", "s");
      } catch (e) {
        alert("Erro ao salvar e recalcular", "e");
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openRecalcModal() {
      this.resetRecalc();
      this.$refs.recalcModal.show();
    },
    resetRecalc() {
      this.recalcFromDate = "";
      this.recalcToDate = "";
      this.recalcPreview = [];
    },
    async onRecalcDateChange() {
      if (!this.isValidRecalcDate) {
        this.recalcPreview = [];
        return;
      }
      try {
        const { days } = await stations.getStationManualRecalcPreview(this.recalcFromDate, this.recalcToDate);
        this.recalcPreview = days || [];
      } catch (e) {
        console.error(e);
        this.recalcPreview = [];
      }
    },
    async confirmRecalc() {
      try {
        this.loading = true;
        this.currentLoadingMessage = "Reprocessando estação...";
        const { restored, kept } = await stations.stationManualRecalc(this.recalcFromDate, this.recalcToDate);
        await this.loadWindow();
        let resumo = `${restored} dia(s) voltaram à medição original`;
        if (!restored) resumo = "nenhum dia tinha medição da estação, nada foi alterado";
        if (kept) resumo += `, ${kept} mantido(s)`;
        alert(`${resumo}. Manejo recalculado.`, "s");
      } catch (e) {
        alert("Erro ao reprocessar", "e");
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.saveButton {
  background-color: var(--green-3v3);
  color: white;
  max-width: 220px;
}
.saveButton:hover {
  background-color: var(--submit-button-form-dark);
}
/* Janelas longas rendem centenas de dias: rola a partir da 4ª linha em vez de esticar o modal. */
.day-block {
  max-height: calc(4 * 1.5em + 1rem);
  overflow-y: auto;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.5rem 0.5rem 1.6rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
  font-size: 0.9em;
  line-height: 1.5;
}
.day-block--flush {
  max-height: calc(4 * 1.5em);
  padding: 0 0 0 1.1rem;
  border: 0;
  background-color: transparent;
}
/* Estado da linha, com o par ímpar/par preservando o zebrado do Tabulator e o hover 20% mais escuro. */
.wrapper-data-table >>> .tabulator-row.st-locked {
  background-color: #c7c7c7;
}
.wrapper-data-table >>> .tabulator-row.st-locked.tabulator-row-even {
  background-color: #bababa;
}
.wrapper-data-table >>> .tabulator-row.st-locked:hover {
  background-color: #9f9f9f;
}
.wrapper-data-table >>> .tabulator-row.st-locked.tabulator-row-even:hover {
  background-color: #959595;
}
.wrapper-data-table >>> .tabulator-row.st-missing {
  background-color: #fff6da;
}
.wrapper-data-table >>> .tabulator-row.st-missing.tabulator-row-even {
  background-color: #f1e8cc;
}
.wrapper-data-table >>> .tabulator-row.st-missing:hover {
  background-color: #ccc5ae;
}
.wrapper-data-table >>> .tabulator-row.st-missing.tabulator-row-even:hover {
  background-color: #c1baa3;
}
.wrapper-data-table >>> .tabulator-row.st-edited {
  background-color: #d8e9df;
}
.wrapper-data-table >>> .tabulator-row.st-edited.tabulator-row-even {
  background-color: #cbdcd3;
}
.wrapper-data-table >>> .tabulator-row.st-edited:hover {
  background-color: #adbab2;
}
.wrapper-data-table >>> .tabulator-row.st-edited.tabulator-row-even:hover {
  background-color: #a2b0a9;
}
/* Cobertura sem medição horária: mesmo cinza (e mesmo par ímpar/par) do dia bloqueado para escrita. */
.wrapper-data-table >>> .tabulator-row .tabulator-cell.cell-frozen {
  background-color: #c7c7c7;
}
.wrapper-data-table >>> .tabulator-row.tabulator-row-even .tabulator-cell.cell-frozen {
  background-color: #bababa;
}
.wrapper-data-table >>> .tabulator-row:hover .tabulator-cell.cell-frozen {
  background-color: #9f9f9f;
}
.wrapper-data-table >>> .tabulator-row.tabulator-row-even:hover .tabulator-cell.cell-frozen {
  background-color: #959595;
}
/* Colunas cabem com fitColumns: remove o scroll horizontal sem uso (mantém o vertical). */
.wrapper-data-table {
  overflow-x: hidden !important;
}
.wrapper-data-table >>> .tabulator-tableHolder {
  overflow-x: hidden;
}
</style>
